import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Fade,
  Alert,
  useTheme,
  SelectChangeEvent,
  Divider,
  Avatar,
  IconButton,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Payment as PaymentIcon,
  DirectionsCar as CarIcon,
  Photo as PhotoIcon,
  Build as GenericIcon,
} from '@mui/icons-material';

import { ExpenseCategory, EXPENSE_CATEGORIES, EXPENSE_CATEGORY_COLORS, Vehicle } from '../../types';
// @ts-ignore
import { format, set } from 'date-fns';
import expenseService from '../../services/expenseService';
import vehicleService from '../../services/vehicleService';

// Componente para exibir o ícone da categoria de despesa
const CategoryIcon = ({ category }: { category: ExpenseCategory }) => {
  switch (category) {
    case 'maintenance':
      return <GenericIcon />;
    case 'mechanics':
      return <GenericIcon />;
    case 'bodywork':
      return <GenericIcon />;
    case 'upholstery':
      return <GenericIcon />;
    case 'fuel':
      return <GenericIcon />;
    case 'insurance':
      return <GenericIcon />;
    case 'tax':
      return <GenericIcon />;
    case 'accessories':
      return <GenericIcon />;
    case 'cleaning':
      return <GenericIcon />;
    case 'other':
      return <GenericIcon />;
    default:
      return <GenericIcon />;
  }
};

interface ExpenseFormData {
  vehicle_id: number;
  category: ExpenseCategory;
  description: string;
  amount: number;
  expense_date: string;
  documento?: File;
  notes?: string;
  type: string;
  status: string;
  url_documento?: string;
  /** true = reduz o caixa da loja; false = só impacta o lucro do veículo */
  deduct_from_store_cash: boolean;
}

const ExpenseForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  // Obter vehicle_id da URL se existir
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedVehicleId = queryParams.get('vehicle_id');
  const prefillDescription = queryParams.get('description');
  const prefillAmount = queryParams.get('amount');
  const prefillNotes = queryParams.get('notes');
  const prefillCategory = queryParams.get('category');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ExpenseFormData>({
    vehicle_id: preselectedVehicleId ? Number(preselectedVehicleId) : 0,
    category: (prefillCategory as any) || 'maintenance',
    description: prefillDescription ? String(prefillDescription) : '',
    amount: prefillAmount ? (parseFloat(String(prefillAmount).replace(',', '.')) || 0) : 0,
    // date: format(new Date(), 'yyyy-MM-dd'),
    notes: prefillNotes ? String(prefillNotes) : '',
    type: 'expense', /*Não usado*/
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    status: '',
    url_documento: '',
    deduct_from_store_cash: true,
  });
  const [initialFormData, setInitialFormData] = useState<ExpenseFormData>({
    vehicle_id: preselectedVehicleId ? Number(preselectedVehicleId) : 0,
    category: (prefillCategory as any) || 'maintenance',
    description: prefillDescription ? String(prefillDescription) : '',
    amount: prefillAmount ? (parseFloat(String(prefillAmount).replace(',', '.')) || 0) : 0,
    // date: format(new Date(), 'yyyy-MM-dd'),
    notes: prefillNotes ? String(prefillNotes) : '',
    type: 'expense', /*Não usado*/
    expense_date: '',
    status: '',
    url_documento: '',
    deduct_from_store_cash: true,
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [urlDocumento, setUrlDocumento] = useState<string | null>(null);

  // Carregar dados iniciais
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Carregar veículos disponíveis 
        const vehiclesData = await vehicleService.getAllVehicles();
        setVehicles(vehiclesData);

        // Se estiver editando, carrega dados da despesa
        if (isEditing) {
          const expenseData = await expenseService.getExpense(parseInt(id!));
          const affectsCash = (expenseData as any).deduct_from_store_cash;
          const deductFromStore = affectsCash !== false && affectsCash !== 0 && affectsCash !== '0';
          setFormData({
            ...expenseData,
            expense_date: expenseData.expense_date,
            type: 'expense', /*Não usado*/
            status: expenseData.status,
            notes: expenseData.notes,
            url_documento: expenseData.url_documento,
            deduct_from_store_cash: deductFromStore,
          });


          setInitialFormData({
            ...expenseData,
            expense_date: expenseData.expense_date,
            type: 'expense', /*Não usado*/
            status: expenseData.status,
            notes: expenseData.notes,
            url_documento: expenseData.url_documento,
            deduct_from_store_cash: deductFromStore,
          });

          if (expenseData.url_documento) {
            setUrlDocumento(expenseData.url_documento)
          }



          setSelectedVehicle(vehiclesData.find(v => v.id === expenseData.vehicle_id) || null);
        }

        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setError('Não foi possível carregar os dados necessários. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      if (name === 'date') {
        updated.expense_date = value;
      }

      return updated;
    });
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };
  
  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    value = value.replace(/[^0-9]/g, '');

    const numericValue = parseFloat(value) / 100; 
    setFormData(prevState => ({
      ...prevState,
      amount: numericValue,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVehicleChange = (_event: any, newValue: Vehicle | null) => {
    setSelectedVehicle(newValue);
    setFormData(prev => ({ ...prev, vehicle_id: newValue?.id || 0 }));
  };
  

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSaving(true);

    // Validar formulário
    if (!formData.vehicle_id || !formData.category || !formData.description || formData.amount <= 0 || !formData.expense_date) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      setSaving(false);
      return;

    }

    setSaving(true);



    // Criação de um novo FormData
    const formAppend = new FormData();

    // Comparar com os dados iniciais e adicionar apenas os campos que foram alterados
    formData.vehicle_id !== initialFormData.vehicle_id && formAppend.append('vehicle_id', String(formData.vehicle_id));
    formData.category !== initialFormData.category && formAppend.append('category', String(formData.category));
    formData.description !== initialFormData.description && formAppend.append('description', formData.description);
    formData.amount !== initialFormData.amount && formAppend.append('amount', String(formData.amount));
    formData.expense_date !== initialFormData.expense_date && formAppend.append('expense_date', formData.expense_date);
    formData.type !== initialFormData.type && formAppend.append('type', formData.type);
    formData.status !== initialFormData.status && formAppend.append('status', formData.status);

    if (file) {
      formAppend.append('documento', file);
    }

    if (formData.notes !== undefined && formData.notes !== initialFormData.notes) {
      formAppend.append('notes', formData.notes);
    }

    formAppend.append('deduct_from_store_cash', formData.deduct_from_store_cash ? '1' : '0');

    try {

      if (isEditing) {
        setSaving(false);

        await expenseService.updateExpense(parseInt(id!), formAppend);
        navigate(`/despesas/${id}`);
        setSaving(false);

      } else {
        // Cria uma nova despesa
        await expenseService.createExpense(formAppend);
        navigate(`/despesas`);
        setSaving(false);

      }
    } catch (error: any) {
      let errorMsg = ''
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      setError(errorMsg);
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }  

  const handleDelete = async () => {

    alert('tem certeza que quer apagr o document ? ');
    const detelte = await expenseService.deleteImageExpense(id);

    if (detelte.status == 204) {
      setPreview(null);
      setUrlDocumento(null)

    }
  };

  const verificarExtensao = (url?: string) => {
    if (!url) {
      return '';
    }
    const ext = url.split('.').pop()?.toLowerCase();
    return ext || '';
  };
  const renderizarConteudo = (input: string | File | null) => {

    if (!input) {
      return <Typography variant="body2" color="textSecondary">Nenhum documento carregado.</Typography>;
    }

    let url = '';
    let tipo = '';

    if (input instanceof File) {
      const generatedURL = URL.createObjectURL(input);
      url = generatedURL;
      tipo = verificarTipoMime(input);
    } else if (typeof input === 'string') {
      url = input;
      if (url.startsWith('blob:')) {
        tipo = 'blob';
      } else {
        tipo = verificarExtensao(url);
      }
    }

    if (tipo === 'image') {
      return <img src={url} alt="Documento" style={{ maxWidth: '100%', maxHeight: '600px' }} />;
    } else if (tipo === 'pdf') {
      return <embed src={url} type="application/pdf" width="100%" height="600px" />;
    } else if (tipo === 'doc') {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Typography variant="body2" color="primary">
            Ver documento (DOC/DOCX)
          </Typography>
        </a>
      );
    } else if (tipo === 'blob') {
      return (
        <Typography variant="body2" color="textSecondary">
          Não conseguimos identificar o tipo do arquivo (possivelmente um Blob).{' '}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Clique aqui para baixar
          </a>
        </Typography>
      );
    } else {
      return (
        <Typography variant="body2" color="textSecondary">
          Tipo de arquivo não suportado para visualização direta.{' '}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Clique aqui para baixar
          </a>
        </Typography>
      );
    }
  };


  const verificarTipoMime = (file: File) => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) {
      return 'image';
    } else if (mimeType === 'application/pdf') {
      return 'pdf';
    } else if (mimeType === 'application/msword' || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'doc';
    }
    return 'unknown';
  };
  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {isEditing ? 'Editar Despesa' : 'Nova Despesa'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/despesas')}
            sx={{ borderRadius: '8px' }}
          >
            Voltar
          </Button>
        </Box>

        {saving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Salvando dados do despesas...
            </Typography>
          </Box>
        )}

        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Seleção de Veículo */}
              <Grid item xs={12}>
                <Box sx={{
                  p: 2,
                  borderRadius: '8px',
                  bgcolor: 'rgba(30, 30, 40, 0.5)',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <CarIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                  <Typography variant="h6">Informações do Veículo</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  id="vehicle-select"
                  options={vehicles}
                  value={selectedVehicle}
                  onChange={handleVehicleChange}
                  getOptionLabel={(option) => `${option.brand} ${option.model} (${option.plate})`}
                  renderOption={(props, option) => {
                    // Remover o `key` de `props` para evitar conflito
                    const { key, ...restProps } = props;  // Removemos o `key` de `props`

                    return (
                      <li key={option.id} {...restProps}>  {/* Passando `key` diretamente */}
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: option.status === 'available'
                                ? theme.palette.success.main
                                : option.status === 'maintenance'
                                  ? theme.palette.warning.main
                                  : theme.palette.primary.main,
                              mr: 1,
                            }}
                          >
                            <CarIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">{`${option.brand} ${option.model}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {`${option.year} • ${option.plate} • ${option.color}`}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }}

                  renderInput={(params) => (
                    <TextField
                      {...params}  // Espalha as outras props corretamente
                      label="Veículo"
                      placeholder="Selecione um veículo"
                      required
                      helperText={formData.vehicle_id === 0 ? "Selecione um veículo" : ""}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CarIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>


              {/* Informações da Despesa */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{
                  p: 2,
                  borderRadius: '8px',
                  bgcolor: 'rgba(30, 30, 40, 0.5)',
                  mb: 1,
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <PaymentIcon sx={{ color: theme.palette.secondary.main, fontSize: 28 }} />
                  <Typography variant="h6">Detalhes da Despesa</Typography>
                </Box>
              </Grid>

              {/* Categoria da despesa */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel id="category-label">Categoria</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleSelectChange}
                    label="Categoria"
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }
                    }}
                  >
                    {Object.entries(EXPENSE_CATEGORIES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: EXPENSE_CATEGORY_COLORS[value as ExpenseCategory]
                            }}
                          >
                            <CategoryIcon category={value as ExpenseCategory} />
                          </Avatar>
                          {label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Adicionar depois da seção para categoria, antes da seção para data */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status da Despesa</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="Status da Despesa"
                  >
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="completed">Concluída</MenuItem>
                    <MenuItem value="paid">Paga</MenuItem>
                    <MenuItem value="cancelled">Cancelada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Data da despesa */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  id="date"
                  name="date"
                  label="Data"
                  type="date"
                  value={formData.expense_date ? new Date(formData.expense_date).toISOString().split('T')[0] : ''}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Valor da despesa */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  id="amount"
                  name="amount"
                  label="Valor (R$)"
                  type="text" 
                  value={formatCurrency(formData.amount)}
                  onChange={handleNumberChange}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend">Origem do desconto</FormLabel>
                  <RadioGroup
                    value={formData.deduct_from_store_cash ? 'store' : 'vehicle_profit'}
                    onChange={(_, v) =>
                      setFormData((prev) => ({
                        ...prev,
                        deduct_from_store_cash: v === 'store',
                      }))
                    }
                  >
                    <FormControlLabel
                      value="store"
                      control={<Radio />}
                      label="Descontar do caixa da loja"
                    />
                    <FormControlLabel
                      value="vehicle_profit"
                      control={<Radio />}
                      label="Descontar apenas do lucro do veículo (não reduz o caixa da loja)"
                    />
                  </RadioGroup>
                  <FormHelperText>
                    No segundo caso, o valor continua ligado ao veículo para cálculo de lucro na venda, mas não entra nas saídas totais do caixa.
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Descrição da despesa */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  id="description"
                  name="description"
                  label="Descrição"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descreva a despesa (ex: Troca de óleo e filtros)"
                />
              </Grid>

              {/* Upload de comprovante */}

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '1px dashed #666',
                    borderRadius: '8px',
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.15)',
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <input
                    type="file"
                    id="receipt"
                    accept="image/*,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <label htmlFor="receipt" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                    {urlDocumento ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                        {renderizarConteudo(urlDocumento)}

                        <Button
                          variant="outlined"
                          color="error"
                          sx={{ marginTop: 2 }}
                          onClick={() => handleDelete()}
                        >
                          Remover documento
                        </Button>
                      </Box>
                    ) : file ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {renderizarConteudo(file)}

                        <Typography variant="body2" color="primary">
                          Clique para trocar o comprovante
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {/* Exibe ícone de arquivo quando não for imagem */}
                        <InsertDriveFileIcon
                          sx={{
                            fontSize: 48,
                            color: theme.palette.primary.main,
                            mb: 1,
                          }}
                        />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Arraste ou clique para anexar um comprovante
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Formatos aceitos: JPG, PNG, PDF, DOC, DOCX (máx. 5MB)
                        </Typography>
                      </Box>
                    )}
                  </label>
                </Box>
              </Grid>

              {/* Observações */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="notes"
                  name="notes"
                  label="Observações"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Observações adicionais sobre a despesa"
                />
              </Grid>


              {/* Botões de ação */}
              <Grid item xs={12}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                    {error}
                  </Alert>
                )}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/despesas')}
                    disabled={saving}
                    sx={{ borderRadius: '8px' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    sx={{
                      py: 1,
                      px: 3,
                      borderRadius: '8px',
                      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                      }
                    }}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    </Fade>
  );
};

export default ExpenseForm; 